import { useMemo, useState, ReactNode, useEffect } from "react";
import { matchPath, useLocation } from "react-router";
import { BreadcrumbContext, BreadcrumbContextData, RegisteredBreadcrumbData } from "./BreadcrumbContext";

/** The site configuration. */
declare const site: {

  /** The company name. */
  readonly companyName?: string;
} | undefined;

/** The home node. */
const HomeNode: RegisteredBreadcrumbData = {
  title: "Home",
  path: "/",
  parentIndex: null,
};

/** The `BreadcrumbRoot` component properties. */
export interface BreadcrumbRootProps {

  /** The component children. */
  readonly children?: ReactNode;
}

/** The breadcrumb component. */
export const BreadcrumbRoot = (props: BreadcrumbRootProps): ReactNode => {
  const { children } = props;

  const [items, setItems] = useState<ReadonlyArray<RegisteredBreadcrumbData>>([HomeNode]);
  const [titleOverride, setTitleOverride] = useState<{ pathname: string; title: string } | null>(null);
  const location = useLocation();

  useEffect(() => {
    setTitleOverride((prevTitleOverride) => {
      if (!prevTitleOverride) return prevTitleOverride;
      if (prevTitleOverride.pathname === location.pathname) return prevTitleOverride;
      return null;
    });
  }, [location]);

  const nodes = useMemo((): ReadonlyArray<RegisteredBreadcrumbData> => {
    let node = items.find((entry) => {
      const { path } = entry;
      const { pathname } = location;

      if (!matchPath({ path }, pathname)) return false;

      return true;
    }) || null;

    let result: ReadonlyArray<RegisteredBreadcrumbData> = [];

    while (node) {
      result = [node, ...result];
      node = node.parentIndex !== null ? items[node.parentIndex] : null;
    }

    return result;
  }, [items, location]);

  const contextData = useMemo((): BreadcrumbContextData => {
    return {
      nodes,
      titleOverride,
      setItems: (items): (() => void) => {
        setItems([HomeNode, ...items]);

        return (): void => {
          setItems((previousItems) => {
            if (previousItems.length === 1) return previousItems;
            return [HomeNode];
          });
        };
      },
      setTitle: (title): void => {
        const { pathname } = location;
        setTitleOverride((title) ? { pathname, title } : null);
      },
    };
  }, [nodes, location, titleOverride]);

  // Set the page title according to the breadcrumb.
  useEffect(() => {
    let title = site ? site.companyName : "Capital Rx";
    const lastNode = nodes[nodes.length - 1];
    if (lastNode) title += ` - ${titleOverride ? titleOverride.title : lastNode.title}`;
    if (title !== undefined) document.title = title;
  }, [nodes, titleOverride]);

  return (
    <>
      <BreadcrumbContext.Provider value={contextData}>{children}</BreadcrumbContext.Provider>
    </>
  );
};
